import SemanticDatabaseService from './SemanticDatabaseService';
import SemanticTableService from './SemanticTableService';
import DataSourceService from './DataSourceService';

function extractList(response, ...keys) {
  if (Array.isArray(response)) {
    return response;
  }
  for (const key of keys) {
    if (Array.isArray(response?.[key])) {
      return response[key];
    }
  }
  return [];
}

/**
 * Ingesta multi-hoja (2026-09-13): file-ingestion ahora puede producir N
 * semantic_table_payload (uno por hoja exitosa), acumulados en
 * `sheet_progress`. Si esa lista no existe (conexión ingerida antes de esta
 * feature, o un CSV/Excel de 1 sola hoja donde igual se sigue espejando el
 * campo singular) cae al `semantic_table_payload` de siempre.
 */
function extractTablePayloads(connection) {
  if (Array.isArray(connection?.sheet_progress) && connection.sheet_progress.length > 0) {
    return connection.sheet_progress
      .filter((sheet) => sheet.status === 'success' && sheet.semantic_table_payload)
      .map((sheet) => JSON.parse(sheet.semantic_table_payload));
  }
  return connection?.semantic_table_payload ? [JSON.parse(connection.semantic_table_payload)] : [];
}

/**
 * Agrupa por tabla ORIGEN las relaciones que la ingesta detectó con IA
 * (file-ingestion::_generate_relationships_with_backoff), en el shape de
 * `joins` que acepta semantic_table. La tabla destino se referencia por
 * NOMBRE (`target_table`), así que no hace falta resolver su table_id.
 */
function buildJoinsByTableName(connection) {
  let relationships = [];
  try {
    relationships = connection?.detected_relationships
      ? JSON.parse(connection.detected_relationships)
      : [];
  } catch (error) {
    console.error('detected_relationships inválido, se ignora:', error);
    return {};
  }

  const byTable = {};
  for (const rel of relationships) {
    if (!rel?.source_table || !rel?.source_column || !rel?.target_table || !rel?.target_column) {
      continue;
    }
    if (!byTable[rel.source_table]) {
      byTable[rel.source_table] = [];
    }
    byTable[rel.source_table].push({
      target_table: rel.target_table,
      type: rel.cardinality === 'many_to_many' ? 'inner' : 'left',
      on: [{ source_column: rel.source_column, target_column: rel.target_column }],
      cardinality: rel.cardinality,
      purpose: rel.purpose
    });
  }
  return byTable;
}

/**
 * File_Ingestion_Lambda (whisperbi-back-security) has no Cognito user
 * session, so it can't call whisperbi-back-semantic-model's API directly —
 * it stores the semantic_database/semantic_table payloads it computed
 * (Glue table info + AI-generated column metadata) as JSON on the
 * SourceConnections record instead. Once the frontend sees the connection
 * reach 'active', it persists those payloads here using the real logged-in
 * user's session (same auth pattern as SemanticDatabaseService/SemanticTableService).
 */
const SemanticModelProvisioningService = {
  /**
   * @param {object} connection - raw source connection record (must include
   *   semantic_database_payload, y uno o más semantic_table_payload --
   *   ver extractTablePayloads).
   * @returns {Promise<{success: boolean, semanticDatabaseId?: string, tableCount?: number, error?: string}>}
   */
  async provisionFromConnection(connection) {
    // Todo el parseo va junto y dentro del try: extractTablePayloads también
    // hace JSON.parse (de cada hoja), y si tira, esta función debe devolver
    // su { success: false, error } de siempre en vez de propagar --
    // reconcileFileConnections depende de eso para no cortar el resto.
    let tablePayloads;
    let databasePayload;
    try {
      tablePayloads = extractTablePayloads(connection);
      databasePayload = connection?.semantic_database_payload
        ? JSON.parse(connection.semantic_database_payload)
        : null;
    } catch (error) {
      return { success: false, error: `Payload de semantic model inválido: ${error.message}` };
    }

    if (!databasePayload || tablePayloads.length === 0) {
      return { success: false, error: 'La conexión no tiene semantic model payload (¿ingesta aún no completada?)' };
    }

    const joinsByTableName = buildJoinsByTableName(connection);
    const accountId = databasePayload.account_id;
    const connectionId = databasePayload.connection_id;

    try {
      // Buscar o crear el semantic_database (uno por conexión, sin importar
      // cuántas hojas/tablas tenga)
      let semanticDatabaseId = null;
      const existingDatabases = await SemanticDatabaseService.list(accountId).catch(() => null);
      const existingDb = extractList(existingDatabases, 'items', 'data', 'semantic_databases')
        .find((db) => db.connection_id === connectionId);

      if (existingDb) {
        semanticDatabaseId = existingDb.semantic_database_id || existingDb.id;
      } else {
        const created = await SemanticDatabaseService.create(databasePayload);
        semanticDatabaseId = created?.semantic_database_id || created?.id;
      }

      if (!semanticDatabaseId) {
        return { success: false, error: 'No se pudo obtener semantic_database_id' };
      }

      // Buscar o crear cada semantic_table (una por hoja exitosa). Match
      // por connection_id + table_name -- ya puede haber N tablas para la
      // misma conexión, connection_id solo no alcanza para identificar cuál.
      const existingTables = await SemanticTableService.list({ accountId, semanticDatabaseId }).catch(() => null);
      const existingTableList = extractList(existingTables, 'items', 'data', 'semantic_tables');

      for (const tablePayload of tablePayloads) {
        const finalTablePayload = { ...tablePayload, semantic_database_id: semanticDatabaseId };
        const existingTable = existingTableList.find(
          (t) => t.connection_id === connectionId && t.table_name === tablePayload.table_name
        );

        if (existingTable) {
          const tableId = existingTable.table_id || existingTable.id;
          // A propósito SIN la clave `joins`: el backend reemplaza el array
          // entero cuando viene (semantic_table/app.py:395-399), así que
          // mandarla acá borraría los joins que el usuario haya editado o
          // agregado a mano -- y esta función corre en cada entrada a la
          // página vía reconcileFileConnections.
          await SemanticTableService.update(tableId, finalTablePayload);
        } else {
          // Los joins detectados se mandan SOLO al crear la tabla: el create
          // los persiste de una (semantic_table/app.py:225) y no se vuelven
          // a tocar nunca. Crearlos aparte con POST .../joins duplicaría uno
          // por cada reconciliación, porque ese endpoint appendea sin
          // deduplicar (app.py:674).
          const joins = joinsByTableName[tablePayload.table_name];
          await SemanticTableService.create(
            joins && joins.length ? { ...finalTablePayload, joins } : finalTablePayload
          );
        }
      }

      return { success: true, semanticDatabaseId, tableCount: tablePayloads.length };
    } catch (error) {
      console.error('Error al aprovisionar el semantic model desde la conexión:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Self-healing safety net for the fragile case where a file (Excel/CSV)
   * connection finishes ingestion but the browser tab that was polling for
   * it got closed/navigated away before provisionFromConnection ran (that
   * poll is an in-memory setTimeout loop in SemanticConnections.jsx, not a
   * durable server-side job — nothing else ever retries it).
   *
   * Scans every 'active'/'ready' file-type connection and (re)provisions
   * its semantic_database/semantic_table if missing. provisionFromConnection
   * is idempotent (finds-or-creates by connection_id), so calling this
   * repeatedly — e.g. on every load of the Conexiones or Entidades page —
   * is safe and only does real work for connections that actually need it.
   *
   * @returns {Promise<{success: boolean, results?: Array, error?: string}>}
   */
  async reconcileFileConnections() {
    let connections;
    try {
      connections = await DataSourceService.fetchDataSources();
    } catch (error) {
      console.error('No se pudieron cargar las conexiones para reconciliar el modelo semántico:', error);
      return { success: false, error: error.message };
    }

    const candidates = (connections || []).filter(
      (c) => c.type === 'Excel' && (c.status === 'active' || c.status === 'ready')
    );

    const results = [];
    for (const candidate of candidates) {
      try {
        const rawConnection = await DataSourceService.getRawSourceConnection(candidate.id);
        const hasMetadata =
          rawConnection?.semantic_database_payload &&
          (rawConnection?.semantic_table_payload || rawConnection?.sheet_progress?.length > 0);
        if (!hasMetadata) {
          continue; // Ingestion metadata not available yet — nothing to reconcile.
        }
        const result = await this.provisionFromConnection(rawConnection);
        if (!result.success) {
          console.error(`Error al reconciliar el modelo semántico de la conexión ${candidate.id}:`, result.error);
        }
        results.push({ connectionId: candidate.id, ...result });
      } catch (error) {
        console.error(`Error al reconciliar el modelo semántico de la conexión ${candidate.id}:`, error);
        results.push({ connectionId: candidate.id, success: false, error: error.message });
      }
    }

    return { success: true, results };
  },

  /**
   * Deletes the semantic_database/semantic_table associated with a file
   * connection, called right before the connection itself is deleted so no
   * orphaned semantic model records are left behind. Soft-deletes (not hard
   * deletes), matching how the connection itself is deleted. Best-effort per
   * record — a failure on one doesn't stop the others from being attempted.
   *
   * @param {string} connectionId
   * @param {string} accountId
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deprovisionForConnection(connectionId, accountId) {
    if (!connectionId || !accountId) {
      return { success: false, error: 'connectionId y accountId son requeridos' };
    }

    try {
      const existingDatabases = await SemanticDatabaseService.list(accountId).catch(() => null);
      const matchingDb = extractList(existingDatabases, 'items', 'data', 'semantic_databases')
        .find((db) => db.connection_id === connectionId);

      if (!matchingDb) {
        return { success: true }; // Nothing was ever provisioned for this connection.
      }

      const semanticDatabaseId = matchingDb.semantic_database_id || matchingDb.id;

      const existingTables = await SemanticTableService.list({ accountId, semanticDatabaseId }).catch(() => null);
      const matchingTables = extractList(existingTables, 'items', 'data', 'semantic_tables')
        .filter((t) => t.connection_id === connectionId);

      let hadError = false;

      for (const table of matchingTables) {
        const tableId = table.table_id || table.id;
        if (!tableId) continue;
        try {
          await SemanticTableService.softDelete(tableId);
        } catch (error) {
          hadError = true;
          console.error(`Error al eliminar la entidad semántica ${tableId}:`, error);
        }
      }

      if (semanticDatabaseId) {
        try {
          await SemanticDatabaseService.softDelete(semanticDatabaseId);
        } catch (error) {
          hadError = true;
          console.error(`Error al eliminar la base semántica ${semanticDatabaseId}:`, error);
        }
      }

      return hadError
        ? { success: false, error: 'No se pudieron eliminar todos los recursos del modelo semántico' }
        : { success: true };
    } catch (error) {
      console.error('Error al desaprovisionar el modelo semántico de la conexión:', error);
      return { success: false, error: error.message };
    }
  }
};

export default SemanticModelProvisioningService;
